import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ImageWithFooter from './ImageWithFooter';
import { isVisible } from '../utils/dynamicFormUtil';
import { useParams } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { getFieldGroups } from '../services/project/fieldGroupsService';

const ViewEngagementModal = ({
  show,
  onClose,
  forms,
  engagement,
  handleImageClick = () => {},
  isNoEditValidationFlow = false,
  validationStatusOptions = { Validated: [], Invalid: [] },
  onSubmitReview,
  onSubmit2ndReview,
}) => {
  const [selectedReviewStatus, setSelectedReviewStatus] = React.useState('');
  const [selectedReviewRemark, setSelectedReviewRemark] = React.useState('');
  const [headerCollapsed, setHeaderCollapsed] = React.useState(true);
  const [isViewingParent, setIsViewingParent] = React.useState(false);
  const [fieldGroupings, setFieldGroupings] = React.useState(null);
  const { projectId } = useParams();
  const { projects } = useProjects();
  
  // Detect if opened from validation page via prop
  const isValidationPage = engagement?.fromValidationPage;
  const hasParentEngagement = Boolean(engagement?.parentEngagement);
  const displayedEngagement = isViewingParent && engagement?.parentEngagement
    ? engagement.parentEngagement
    : engagement;
  
  // Get current project settings
  const currentProject = projects.find(p => p.id === parseInt(projectId, 10));
  const hasValidation = currentProject?.settings?.validation;
  const hasApproval = currentProject?.settings?.approval;
  const availableRemarks = React.useMemo(() => {
    if (selectedReviewStatus === 'Validated') {
      return validationStatusOptions?.Validated || [];
    }
    if (selectedReviewStatus === 'Invalid') {
      return validationStatusOptions?.Invalid || [];
    }
    return [];
  }, [selectedReviewStatus, validationStatusOptions]);

  const targetProjectId = engagement?.projectId || projectId;
  const targetFormGroupId = engagement?.formGroupId || (forms && forms[0]?.formGroupId);

  React.useEffect(() => {
    if (!show || !targetProjectId || !targetFormGroupId) {
      setFieldGroupings(null);
      return;
    }

    let isMounted = true;
    getFieldGroups(targetProjectId, targetFormGroupId)
      .then(fgData => {
        if (isMounted && fgData && Array.isArray(fgData.groups) && fgData.groups.length > 0) {
          setFieldGroupings(fgData);
        }
      })
      .catch(err => {
        console.warn('Could not fetch field groups:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [show, targetProjectId, targetFormGroupId]);

  React.useEffect(() => {
    if (!show) {
      setSelectedReviewStatus('');
      setSelectedReviewRemark('');
      setIsViewingParent(false);
      return;
    }

    setSelectedReviewStatus('');
    setSelectedReviewRemark('');
  }, [show, engagement?.id]);

  // 1) Build previewValues-like map: {[field.id]: value}
  const idKeyedValues = React.useMemo(() => {
    const m = {};
    const addFieldToMap = (f) => {
      if (!f || f.id == null) return;
      const keyId = String(f.id);
      const val =
        displayedEngagement?.fields?.[f.name] ??
        displayedEngagement?.fields?.[f.id] ??
        displayedEngagement?.fields?.[keyId];
      m[keyId] = val;
    };

    (forms || []).forEach(form => {
      (form.fields || []).forEach(addFieldToMap);
    });

    return m;
  }, [forms, displayedEngagement]);

  // Derive effective field groupings (from API or field.config.groupLabel)
  const effectiveFieldGroupings = React.useMemo(() => {
    if (fieldGroupings && Array.isArray(fieldGroupings.groups) && fieldGroupings.groups.length > 0) {
      return fieldGroupings;
    }

    const allFields = (forms || []).flatMap(form => form.fields || []);
    const hasAnyGroupLabel = allFields.some(
      field => field?.config?.groupLabel && String(field.config.groupLabel).trim() !== ''
    );

    if (!hasAnyGroupLabel) return null;

    const groupsMap = new Map();
    const ungroupedFields = [];

    allFields.forEach(field => {
      const groupLabel = field?.config?.groupLabel;
      if (groupLabel && typeof groupLabel === 'string' && groupLabel.trim() !== '') {
        const trimmedLabel = groupLabel.trim();
        if (!groupsMap.has(trimmedLabel)) {
          groupsMap.set(trimmedLabel, []);
        }
        groupsMap.get(trimmedLabel).push(field);
      } else {
        ungroupedFields.push(field);
      }
    });

    let defaultGroupOrderIndex = 1;
    const groups = Array.from(groupsMap.entries()).map(([label, fields]) => {
      const explicitOrder = fields
        .map(f => f?.config?.groupOrder)
        .find(o => typeof o === 'number' && Number.isInteger(o));
      const groupOrder = explicitOrder !== undefined ? explicitOrder : defaultGroupOrderIndex * 100;
      defaultGroupOrderIndex++;
      return {
        groupLabel: label,
        groupOrder,
        fields,
      };
    });

    groups.sort((a, b) => a.groupOrder - b.groupOrder || a.groupLabel.localeCompare(b.groupLabel));

    return { groups, ungroupedFields };
  }, [fieldGroupings, forms]);

  const isApprovalOrValidationScreen = Boolean(isValidationPage || hasApproval || hasValidation);

  const isFieldVisible = React.useCallback((field) => {
    if (!field) return false;
    const parentForm = (forms || []).find(f => f.fields?.some(fld => String(fld.id) === String(field.id)));
    if (parentForm && !isVisible(parentForm, idKeyedValues)) return false;
    return isVisible(field, idKeyedValues);
  }, [forms, idKeyedValues]);

  const renderField = React.useCallback((field) => {
    if (!field || field.type === 'label') return null;
    const value = idKeyedValues[String(field.id)];

    let imageSrc = null;
    let imageFooter = null;
    if (field.type === 'image' && value) {
      if (typeof value === 'string') {
        imageSrc = value;
      } else if (Array.isArray(value)) {
        imageSrc = typeof value[0] === 'string' ? value[0] : value[0]?.uri || value[0]?.url;
        imageFooter = value[0]?.location || value[0]?.footer;
      } else if (typeof value === 'object') {
        imageSrc =
          (Array.isArray(value?.uri) ? value.uri[0] : value?.uri) ||
          (Array.isArray(value?.url) ? value.url[0] : value?.url) ||
          null;
        imageFooter = value?.location || value?.footer || null;
      }
    }

    return (
      <Form.Group key={field.name || field.id} className="mb-3">
        <Form.Label><strong>{field.label}</strong></Form.Label>
        <div>
          {field.type === 'image' ? (
            imageSrc ? (
              <div
                className="image-container"
                onClick={() => handleImageClick(imageSrc, imageFooter)}
                style={{ cursor: 'pointer' }}
              >
                <ImageWithFooter
                  src={imageSrc}
                  alt={field.label}
                  footer={imageFooter}
                  fluid
                  rounded
                  thumbnail
                  style={{ width: 300 }}
                />
              </div>
            ) : (
              <div>—</div>
            )
          ) : field.type === 'address' && value ? (
            <div>
              {value.street && <div>Street: {value.street}</div>}
              {value.barangay && <div>Barangay: {value.barangay}</div>}
              {value.city && <div>City: {value.city}</div>}
              {value.province && <div>Province: {value.province}</div>}
            </div>
          ) : (
            <div>
              {field.type === 'checkbox' 
                ? (value ? 'True' : 'False') 
                : (value ?? '—')
              }
            </div>
          )}
        </div>
      </Form.Group>
    );
  }, [idKeyedValues, handleImageClick]);

  return (
    <Modal show={show} onHide={onClose} size="lg" fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ width: '100%', cursor: 'pointer' }} onClick={() => setHeaderCollapsed(c => !c)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
              <strong>Full Name:</strong>{' '}
              <span className="text-muted">
                {displayedEngagement?.user?.name || `${displayedEngagement?.User?.firstName || ''} ${displayedEngagement?.User?.lastName || ''}`.trim() || '—'}
              </span>
              {!isViewingParent && hasParentEngagement && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-2 align-baseline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsViewingParent(true);
                  }}
                >
                  Original Engagement
                </Button>
              )}
              {isViewingParent && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsViewingParent(false);
                  }}
                >
                  Back
                </Button>
              )}
            </span>
            <span style={{ fontSize: '1rem', marginLeft: '1rem' }}>
              {headerCollapsed ? '▼' : '▲'}
            </span>
          </div>
          {hasApproval && (
            <>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                Approver: {displayedEngagement?.approverName || '—'}
              </div>
              {displayedEngagement?.approvalRemarks && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Approval Remarks: {displayedEngagement.approvalRemarks}
                </div>
              )}
              {displayedEngagement?.approvalTime && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Approval Time: {new Date(displayedEngagement.approvalTime).toLocaleString()}
                </div>
              )}
            </>
          )}
          {hasValidation && (
            <>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                1st Validator: {displayedEngagement?.['1st_validator'] || displayedEngagement?.validatorName || '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                1st Validation Status: {displayedEngagement?.['1st_status'] || displayedEngagement?.['1st_validation'] || '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                1st Validation Description: {displayedEngagement?.['1st_description'] || displayedEngagement?.validationRemarks || '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                2nd Validator: {displayedEngagement?.['2nd_validator'] || displayedEngagement?.secondValidatorName || '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                2nd Validation Status: {displayedEngagement?.['2nd_status'] || displayedEngagement?.['2nd_validation'] || '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                2nd Validation Description: {displayedEngagement?.['2nd_description'] || '—'}
              </div>
              {displayedEngagement?.validationRemarks && !displayedEngagement?.['1st_description'] && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  1st Validation Remarks: {displayedEngagement.validationRemarks}
                </div>
              )}
              {displayedEngagement?.validationTime && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  Validation Time: {new Date(displayedEngagement.validationTime).toLocaleString()}
                </div>
              )}
            </>
          )}
          {!headerCollapsed && (
            <>
              <div>
                <small className="text-muted">
                  Synced Date: {displayedEngagement?.createdAt ? new Date(displayedEngagement.createdAt).toLocaleString() : '—'}
                </small>
              </div>
              <div>
                <small className="text-muted">
                  Submitted Date:{' '}
                  {displayedEngagement?.submittedAt ? new Date(displayedEngagement.submittedAt).toLocaleString() : '—'}
                </small>
              </div>
              <div>
                <small className="text-muted">App Version: {displayedEngagement?.fields?.appVersion || 'unknown'}</small>
              </div>
              <div>
                <small className="text-muted">Signature</small>
                {displayedEngagement?.signature && (
                  <img
                    src={displayedEngagement?.signature}
                    alt="Signature"
                    style={{ maxHeight: 120, objectFit: 'contain', display: 'block' }}
                  />
                )}
              </div>
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="d-flex flex-column gap-3"
        style={{ height: '100%', maxHeight: '80vh', overflowY: 'auto' }}
      >
        {isApprovalOrValidationScreen && effectiveFieldGroupings && Array.isArray(effectiveFieldGroupings.groups) ? (
          <>
            {effectiveFieldGroupings.groups.map((group, groupIdx) => {
              const visibleGroupFields = (group.fields || []).filter(isFieldVisible);
              if (visibleGroupFields.length === 0) return null;
              return (
                <div key={`group-${groupIdx}-${group.groupLabel}`} className="mb-4">
                  <h5 className="border-bottom pb-1 mb-3">{group.groupLabel}</h5>
                  {visibleGroupFields.map(renderField)}
                </div>
              );
            })}
            {(() => {
              const visibleUngrouped = (effectiveFieldGroupings.ungroupedFields || []).filter(isFieldVisible);
              if (visibleUngrouped.length === 0) return null;
              return (
                <div key="group-ungrouped" className="mb-4">
                  <h5 className="border-bottom pb-1 mb-3">Other Fields</h5>
                  {visibleUngrouped.map(renderField)}
                </div>
              );
            })()}
          </>
        ) : (
          (forms || [])
            .filter(form => isVisible(form, idKeyedValues))
            .map(form => (
              <div key={form.id} className="mb-4">
                <h5 className="border-bottom pb-1 mb-3">{form.name}</h5>

                {(form.fields || [])
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map(field => {
                    if (!isFieldVisible(field) || field.type === 'label') return null;
                    return renderField(field);
                  })}
              </div>
            ))
        )}
        {/* Validation page actions */}
        {isValidationPage && !isViewingParent && (
          <div className="mt-4">
            {isNoEditValidationFlow ? (
              (engagement?.['1st_validation'] === 'approved' && engagement?.['2nd_validation'] == null) && (
              <>
                <div className="mb-2">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={selectedReviewStatus}
                    onChange={e => {
                      setSelectedReviewStatus(e.target.value);
                      setSelectedReviewRemark('');
                    }}
                  >
                    <option value="">Select status</option>
                    <option value="Validated">Validated</option>
                    <option value="Invalid">Invalidated</option>
                  </Form.Select>
                </div>
                <div className="mb-2">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Select
                    value={selectedReviewRemark}
                    onChange={e => setSelectedReviewRemark(e.target.value)}
                    disabled={!selectedReviewStatus}
                  >
                    <option value="">Select remark</option>
                    {availableRemarks.map((item, index) => (
                      <option key={`${item}-${index}`} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="success"
                    disabled={!selectedReviewStatus || !selectedReviewRemark}
                    onClick={() => onSubmit2ndReview?.(engagement, {
                      status: selectedReviewStatus,
                      remark: selectedReviewRemark,
                    })}
                  >
                    Submit Review
                  </Button>
                </div>
              </>
              )
            ) : (
              <>
                <div className="mb-2">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={selectedReviewStatus}
                    onChange={e => {
                      setSelectedReviewStatus(e.target.value);
                      setSelectedReviewRemark('');
                    }}
                  >
                    <option value="">Select status</option>
                    <option value="Validated">Validated</option>
                    <option value="Invalid">Invalidated</option>
                  </Form.Select>
                </div>
                <div className="mb-2">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Select
                    value={selectedReviewRemark}
                    onChange={e => setSelectedReviewRemark(e.target.value)}
                    disabled={!selectedReviewStatus}
                  >
                    <option value="">Select remark</option>
                    {availableRemarks.map((item, index) => (
                      <option key={`${item}-${index}`} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="success"
                    disabled={!selectedReviewStatus || !selectedReviewRemark}
                    onClick={() => {
                      onSubmitReview?.(engagement, {
                        status: selectedReviewStatus,
                        remark: selectedReviewRemark,
                      });
                    }}
                  >
                    Submit Review
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViewEngagementModal;
